import React, { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import './AnalyticsView.css';

const AnalyticsView = ({ events, clubs, gymnasts, scores, selectedClub, setSelectedClub }) => {
  // Filtrar datos por club seleccionado
  const filteredGymnasts = useMemo(() => {
    if (!selectedClub) return [];
    return gymnasts.filter(g => g.club_id === selectedClub);
  }, [gymnasts, selectedClub]);

  const filteredScores = useMemo(() => {
    if (!selectedClub) return [];
    return scores.filter(s => s.club_id === selectedClub);
  }, [scores, selectedClub]);

  // Datos de rendimiento por atleta
  const athletePerformanceData = useMemo(() => {
    if (!filteredGymnasts || filteredGymnasts.length === 0) return [];
    
    const performanceMap = {};
    
    filteredGymnasts.forEach(gymnast => {
      const athleteScores = filteredScores.filter(s => s.gymnast_id === gymnast.id) || [];
      if (athleteScores.length > 0) {
        const avgScore = athleteScores.reduce((sum, s) => sum + (s.score || 0), 0) / athleteScores.length;
        performanceMap[gymnast.id] = {
          name: gymnast.name,
          average: parseFloat(avgScore.toFixed(2)),
          count: athleteScores.length,
          maxScore: Math.max(...athleteScores.map(s => s.score || 0)),
          minScore: Math.min(...athleteScores.map(s => s.score || 0))
        };
      }
    });

    return Object.values(performanceMap)
      .sort((a, b) => b.average - a.average)
      .slice(0, 10);
  }, [filteredGymnasts, filteredScores]);

  // Distribución de puntuaciones
  const scoreDistributionData = useMemo(() => {
    if (!filteredScores || filteredScores.length === 0) return [];

    const ranges = {
      '0-2': 0,
      '2-4': 0,
      '4-6': 0,
      '6-8': 0,
      '8-10': 0
    };

    filteredScores.forEach(score => {
      const value = score.score || 0;
      if (value < 2) ranges['0-2']++;
      else if (value < 4) ranges['2-4']++;
      else if (value < 6) ranges['4-6']++;
      else if (value < 8) ranges['6-8']++;
      else ranges['8-10']++;
    });

    return Object.entries(ranges).map(([range, count]) => ({
      range,
      count
    }));
  }, [filteredScores]);

  // Estadísticas por aparato
  const apparatusStatsData = useMemo(() => {
    if (!filteredScores || filteredScores.length === 0) return [];

    const statsMap = {};
    
    filteredScores.forEach(score => {
      if (score.apparatus) {
        if (!statsMap[score.apparatus]) {
          statsMap[score.apparatus] = { name: score.apparatus, scores: [] };
        }
        statsMap[score.apparatus].scores.push(score.score || 0);
      }
    });

    return Object.values(statsMap)
      .map(app => ({
        ...app,
        average: parseFloat((app.scores.reduce((a, b) => a + b, 0) / app.scores.length).toFixed(2)),
        count: app.scores.length
      }));
  }, [filteredScores]);

  // Obtener club seleccionado
  const club = clubs.find(c => c.id === selectedClub);

  return (
    <div className="analytics-view">
      <div className="analytics-header">
        <h1>📊 Análisis de Rendimiento por Club</h1>
        <p>Estadísticas de puntuaciones y desempeño de atletas</p>
      </div>

      {/* Selector de Club */}
      <div className="analytics-selector">
        <label htmlFor="club-select">Selecciona un club:</label>
        <select 
          id="club-select"
          value={selectedClub || ''}
          onChange={(e) => setSelectedClub(e.target.value || null)}
          className="club-select"
        >
          <option value="">-- Seleccionar club --</option>
          {clubs.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {!selectedClub ? (
        <div className="analytics-empty">
          <p>Selecciona un club para ver el análisis de rendimiento</p>
        </div>
      ) : (
        <>
          {/* Grid de gráficos */}
          <div className="analytics-grid">
            {/* Atletas del Club */}
            <div className="analytics-card">
              <h2>Atletas del Club: {club?.name}</h2>
              <div className="athletes-list">
                {filteredGymnasts.length > 0 ? (
                  <ul>
                    {filteredGymnasts.map(g => (
                      <li key={g.id}>{g.name}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-data">No hay atletas en este club</p>
                )}
              </div>
            </div>

            {/* Rendimiento por Atleta */}
            <div className="analytics-card">
              <h2>Rendimiento por Atleta</h2>
              {athletePerformanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={athletePerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Bar dataKey="average" fill="#6f86d6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="no-data">Sin datos</p>
              )}
            </div>

            {/* Distribución de Puntuaciones */}
            <div className="analytics-card">
              <h2>Distribución de Puntuaciones</h2>
              {scoreDistributionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={scoreDistributionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="range" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#fbc658" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="no-data">Sin datos</p>
              )}
            </div>

            {/* Comparación de Aparatos */}
            <div className="analytics-card">
              <h2>Promedio por Aparato</h2>
              {apparatusStatsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={apparatusStatsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Bar dataKey="average" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="no-data">Sin datos</p>
              )}
            </div>

            {/* Estadísticas Resumen */}
            <div className="analytics-card stats-summary">
              <h2>Estadísticas del Club</h2>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{filteredGymnasts?.length || 0}</div>
                  <div className="stat-label">Atletas</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{filteredScores?.length || 0}</div>
                  <div className="stat-label">Puntuaciones</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    {filteredScores && filteredScores.length > 0
                      ? (filteredScores.reduce((sum, s) => sum + (s.score || 0), 0) / filteredScores.length).toFixed(2)
                      : 0}
                  </div>
                  <div className="stat-label">Promedio</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    {filteredScores && filteredScores.length > 0
                      ? Math.max(...filteredScores.map(s => s.score || 0)).toFixed(2)
                      : 0}
                  </div>
                  <div className="stat-label">Máximo</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsView;

